// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalFooter } from '@heroui/react';
import { Button } from '@/components/ui/button';
import { type ModalProps } from '@/stores/use-modal-store.ts';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { API_BASE } from '@/config.ts';

export const DeleteServerModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { server } = data;

  const [isLoading, setIsLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const socket = useSocket();

  const REQUIRED_TEXT = 'Я ХОЧУ УДАЛИТЬ ЭТОТ СЕРВЕР';
  const isConfirmed = confirmText === REQUIRED_TEXT;

  const onClick = async () => {
    try {
      setIsLoading(true);

      const response = await fetch(`${API_BASE}/api/servers/${server?.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      onClose();
      socket?.send(
        JSON.stringify({
          type: 'server-delete',
          payload: { id: server?.id, server: data },
        })
      );
      navigate(`/?deleted-server-id=${server?.id}`);
    } catch {
      // TODO: translate
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="text-center text-2xl font-bold">{t('DeleteServerModal.title')}</div>
              <div className="text-center text-zinc-500">
                {t('DeleteServerModal.description')} <br />
                <span className="font-semibold text-indigo-500">{server?.name}</span>{' '}
                {t('DeleteServerModal.permanentDelete')}.
              </div>
            </ModalHeader>
            <div className="px-6 pb-2">
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={REQUIRED_TEXT}
                className="w-full border rounded-md px-3 py-2 text-sm"
                disabled={isLoading}
              />
              <div className="text-xs text-gray-500 mt-1">
                Введите в поле <b>{REQUIRED_TEXT}</b> для удаления
              </div>
            </div>
            <ModalFooter>
              <Button
                disabled={isLoading}
                onClick={() => {
                  setConfirmText('');
                  onClose();
                }}
                variant="ghost"
              >
                {t('DeleteServerModal.cancel')}
              </Button>
              <Button disabled={isLoading || !isConfirmed}
                      color="danger"
                      onClick={onClick}>
                {t('DeleteServerModal.confirm')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
